import { useCallback, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useHistory } from 'react-router-dom';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Switch,
} from '@accure/ui';
import { zodResolver } from '@hookform/resolvers/zod';
import { Trash2 } from 'lucide-react';
import { useBoolean } from 'usehooks-ts';

import {
  type AlertNotificationRule,
  type CreateAlertNotificationRule,
  NotificationChannel,
  NotificationFrequency,
} from '@/api/fe-api/domain/entities/alert-notifications';
import { CreateRule } from '@/api/fe-api/view/mutations/alert-notifications/create-rule';
import { DeleteRule } from '@/api/fe-api/view/mutations/alert-notifications/delete-rule';
import { UpdateRule } from '@/api/fe-api/view/mutations/alert-notifications/update-rule';
import { GetAllSites } from '@/api/fe-api/view/queries/get-all-sites';
import {
  RadioGroup,
  RadioGroupItem,
} from '@/communication/components/ui/radio-group';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { DialogBody, DialogTitle } from '@/components/ui/dialog';
import { useMe } from '@/hooks/use-me';
import { toastResult } from '@/utils/toast';

import { TooltipAdditionalRecipients } from '../tooltip-additional-recipients';

import {
  defaultValues,
  type FormCreateInputs,
  formCreateSchema,
} from './form-create-notification-rule';
import { InputRecipients } from './input-recipients';
import { MultiSelectAsset } from './multi-select-asset';
import { SelectMinAlertStatus } from './select-min-alert-status';

export type FormEditNotificationRuleProps = {
  loadedData?: AlertNotificationRule;
};

export function FormEditNotificationRule({
  loadedData,
}: FormEditNotificationRuleProps) {
  const history = useHistory();
  const showConfirmDialog = useBoolean();
  const updateRule = UpdateRule.useMutation();
  const createRule = CreateRule.useMutation();
  const deleteRule = DeleteRule.useMutation();
  const { data } = GetAllSites.useQuery();
  const { config } = useMe();

  const startValues: Partial<FormCreateInputs> = {
    ...defaultValues,
    ...(loadedData && {
      enabled: loadedData.isEnabled,
      title: loadedData.description,
      minAlertStatus: loadedData.minAlertStatus,
      assets: loadedData.siteIds,
      channels: Object.fromEntries(
        Object.values(NotificationChannel).map((channel) => [
          channel,
          loadedData.channels.includes(channel),
        ]),
      ) as Record<NotificationChannel, boolean>,
      recipients: loadedData.externalEmails,
    }),
  };

  const form = useForm<FormCreateInputs>({
    defaultValues: startValues,
    resolver: zodResolver(formCreateSchema),
    mode: 'onChange',
  });

  const { isValid } = form.formState;

  const selectAllAssets = useCallback(() => {
    if (data) {
      form.setValue(
        'assets',
        data.map((asset) => asset.id),
      );
      form.trigger();
    }
  }, [data, form]);

  const saveExistingRule = async (input: AlertNotificationRule) => {
    try {
      const result = await toastResult(updateRule.mutateAsync(input), {
        success: 'Changes saved',
        loading: 'Saving...',
        error: 'Failed to save changes',
        exception: 'Failed to save changes',
      });
      if (result.isOk()) {
        history.push(generatePath('/account/alert-notifications'));
      }
    } catch (error) {
      console.error('Failed to save rule changes: ', error);
    }
  };

  const saveNewRule = async (input: CreateAlertNotificationRule) => {
    try {
      const result = await toastResult(createRule.mutateAsync(input), {
        success: 'Notification rule created',
        loading: 'Saving...',
        error: 'Failed to save changes',
        exception: 'Failed to save changes',
      });

      if (result.isOk()) {
        history.push(generatePath('/account/alert-notifications'));
      }
    } catch (error) {
      console.error('Failed to save new rule: ', error);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const result = await toastResult(deleteRule.mutateAsync({ id }), {
        success: 'Rule deleted',
        loading: 'Saving...',
        error: 'Failed to delete rule',
        exception: 'Failed to delete rule',
      });

      if (result.isOk()) {
        history.push(generatePath('/account/alert-notifications'));
      }
    } catch (error) {
      console.error('Failed to delete rule: ', error);
    }
  };

  const handleSave = (formData: FormCreateInputs) => {
    const input: Omit<AlertNotificationRule, 'id'> = {
      isEnabled: formData.enabled,
      minAlertStatus: formData.minAlertStatus,
      siteIds:
        data && data.length === formData.assets.length ? [] : formData.assets,
      channels: Object.entries(formData.channels)
        .filter(([_, isChecked]) => isChecked)
        .map(([status]) => status as NotificationChannel),
      externalEmails: formData.recipients,
      description: formData.title,
      frequency: formData.frequency as NotificationFrequency,
    };

    if (loadedData) {
      saveExistingRule({
        ...input,
        id: loadedData.id,
      });
    } else {
      saveNewRule({
        ...input,
      });
    }
  };

  useEffect(() => {
    form.trigger();
  }, [form]);

  useEffect(() => {
    if (!loadedData || loadedData.siteIds.length === 0) {
      selectAllAssets();
    }
  }, [selectAllAssets, loadedData]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleSave)}>
        <div className="space-y-2 pb-4">
          <FormField
            control={form.control}
            name="title"
            render={({ field }) => (
              <FormItem>
                <FormControl>
                  <Input
                    className="!text-lg"
                    placeholder={`Title (e.g., 'Critical alerts for all assets')`}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>

        <div className="max-h-[75vh] space-y-6 overflow-y-auto p-1">
          <div className="space-y-1">
            <FormField
              control={form.control}
              name="enabled"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-4">
                    <FormLabel className="text-base">Enabled</FormLabel>
                    <FormControl>
                      <Switch value={field.value} onChange={field.onChange} />
                    </FormControl>
                  </div>
                  <div className="text-sm italic text-neutral-400">
                    {field.value
                      ? 'Notifications are enabled'
                      : 'Notifications are disabled'}
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">Frequency of notifications</h3>
            <FormField
              control={form.control}
              name="frequency"
              render={({ field }) => (
                <FormItem className="flex items-center gap-4">
                  <FormControl>
                    <RadioGroup
                      value={field.value}
                      onValueChange={field.onChange}
                    >
                      <div className="flex items-center gap-3">
                        <RadioGroupItem
                          value={'NewAlerts'}
                          id="frequency_NewAlerts"
                        />
                        <label htmlFor="frequency_NewAlerts">
                          <div>Only on new alerts</div>
                          <div className="text-sm italic text-neutral-400">
                            Only get notified when a new issue is detected.
                          </div>
                        </label>
                      </div>
                      <div className="flex items-center gap-3">
                        <RadioGroupItem value={'Daily'} id="frequency_Daily" />
                        <label htmlFor="frequency_Daily">
                          <div>Daily report</div>
                          <div className="text-sm italic text-neutral-400">
                            Get a full status overview every day, even with no
                            new alerts.
                          </div>
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="space-y-2">
            <h3 className="font-medium">Alert Severity</h3>
            <FormField
              control={form.control}
              name="minAlertStatus"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <SelectMinAlertStatus
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {config.type === 'mobility' ? null : (
            <div className="space-y-2">
              <div className="flex items-baseline justify-between">
                <h3 className="font-medium">Assets</h3>
                <button
                  type="button"
                  className="cursor-pointer text-xs text-primary-500 hover:text-primary-600"
                  onClick={selectAllAssets}
                >
                  Select all
                </button>
              </div>
              <FormField
                control={form.control}
                name="assets"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <MultiSelectAsset
                        value={field.value}
                        onChange={field.onChange}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          )}

          <div className="space-y-2">
            <h3 className="flex items-center gap-2 font-medium">
              Recipients
              <TooltipAdditionalRecipients />
            </h3>
            <FormField
              control={form.control}
              name="recipients"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <InputRecipients
                      hasLoadedData={Boolean(loadedData)}
                      value={field.value}
                      onChange={field.onChange}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </div>

        <div className="mt-4 flex justify-between border-t pt-4">
          <div className="flex gap-2">
            {loadedData ? (
              <Button
                type="button"
                onClick={showConfirmDialog.setTrue}
                variant="link"
                className="flex items-center gap-2 text-xs text-neutral-500 hover:text-red-600"
              >
                <Trash2 className="size-4" />
                <span>Delete notification rule</span>
              </Button>
            ) : null}
          </div>
          <div className="flex gap-2">
            <Button type="submit" disabled={!isValid}>
              {loadedData ? 'Save changes' : 'Save notification rule'}
            </Button>
          </div>
        </div>
      </form>
      {loadedData ? (
        <ConfirmDialog
          open={showConfirmDialog.value}
          submitText="Delete"
          onSubmit={async () => handleDelete(loadedData.id)}
          onClose={showConfirmDialog.setFalse}
        >
          <DialogTitle>Delete Notification Rule</DialogTitle>
          <DialogBody>
            <p>Are you sure you want to delete this notification rule?</p>
            <p className="my-4 p-2 text-sm text-neutral-500">
              {loadedData.description}
            </p>
            <p>
              This action cannot be undone. The rule will be permanently removed
              from your settings.
            </p>
          </DialogBody>
        </ConfirmDialog>
      ) : null}
    </Form>
  );
}
